/* ==========================================================================
   Error Boundary Component
   Task F26: Catches unhandled render errors and displays a friendly fallback UI
   ========================================================================== */

import { Component, type ErrorInfo, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { Button } from '@/components/ui/Button'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface ErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode
  /** Optional fallback component to render on error */
  fallback?: ReactNode
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/* --------------------------------------------------------------------------
   Error Boundary Component
   -------------------------------------------------------------------------- */

/**
 * React Error Boundary that catches JavaScript errors anywhere in the
 * child component tree, logs them to Sentry, and displays a fallback UI.
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 *
 * // With custom fallback
 * <ErrorBoundary fallback={<CustomErrorPage />}>
 *   <RiskyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to Sentry in non-development environments
    const environment = import.meta.env.VITE_ENVIRONMENT || 'development'
    if (environment !== 'development') {
      Sentry.captureException(error, {
        extra: {
          componentStack: errorInfo.componentStack,
        },
      })
    }

    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    // Call optional error handler
    this.props.onError?.(error, errorInfo)
  }

  handleRetry = (): void => {
    // Reset error state to retry rendering
    this.setState({ hasError: false, error: null })
  }

  handleGoHome = (): void => {
    // Navigate to home page and reload
    window.location.href = '/'
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Render custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return <ErrorFallback onRetry={this.handleRetry} onGoHome={this.handleGoHome} />
    }

    return this.props.children
  }
}

/* --------------------------------------------------------------------------
   Error Fallback UI
   -------------------------------------------------------------------------- */

interface ErrorFallbackProps {
  /** Handler for retry button */
  onRetry: () => void
  /** Handler for go home button */
  onGoHome: () => void
}

/**
 * Default error fallback component displayed when an error is caught.
 * Shows a friendly message with options to retry or go home.
 */
function ErrorFallback({ onRetry, onGoHome }: ErrorFallbackProps) {
  return (
    <div className="min-h-screen bg-[var(--color-warm-beige)] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
        {/* Error Icon */}
        <div className="mx-auto w-16 h-16 bg-[var(--color-error-light)] rounded-full flex items-center justify-center mb-6">
          <AlertTriangle
            className="w-8 h-8 text-[var(--color-error)]"
            aria-hidden="true"
          />
        </div>

        {/* Error Title */}
        <h1 className="text-2xl font-serif text-[var(--color-charcoal)] mb-3">
          Something went wrong
        </h1>

        {/* Error Description */}
        <p className="text-[var(--color-charcoal-light)] mb-8">
          We're sorry, but something unexpected happened. Please try again or
          return to the home page.
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="primary"
            onClick={onRetry}
            className="inline-flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
            Try Again
          </Button>
          <Button
            variant="secondary"
            onClick={onGoHome}
            className="inline-flex items-center gap-2"
          >
            <Home className="w-4 h-4" aria-hidden="true" />
            Go to Home
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ErrorBoundary
